import type { ReactNode } from "react";

/** Piezas de UI compartidas por las pantallas del portal. */

export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded border border-line bg-panel p-4 sm:p-6 ${className}`}>{children}</div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-xs uppercase tracking-wider text-mute">
        {label}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-mute">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  );
}

export const inputClass =
  "w-full rounded border border-line bg-bg px-3 py-2 font-mono text-sm text-ink outline-none focus:border-accent";

export function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "primary" | "ghost" | "danger";
  disabled?: boolean;
  title?: string;
}) {
  const base =
    "rounded px-3 py-2 font-mono text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50";
  const styles = {
    primary: "bg-accent text-bg hover:opacity-90",
    ghost: "border border-line text-ink hover:border-accent hover:text-accent",
    danger: "border border-danger text-danger hover:bg-danger hover:!text-bg",
  }[variant];
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={`${base} ${styles}`}>
      {children}
    </button>
  );
}

export function Badge({
  children,
  tone = "mute",
}: {
  children: ReactNode;
  tone?: "mute" | "ok" | "warn" | "danger";
}) {
  const styles = {
    mute: "border-line text-mute",
    ok: "border-success text-success",
    warn: "border-amber-500 text-amber-500",
    danger: "border-danger text-danger",
  }[tone];
  return (
    <span
      className={`inline-block rounded border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider ${styles}`}
    >
      {children}
    </span>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="rounded border border-danger/50 bg-danger/10 px-3 py-2 text-sm text-danger">
      {children}
    </p>
  );
}

// Nombre legible de cada campo, para poder decir QUÉ falla en vez de un
// "revisa los campos" que no ayuda a nadie.
const FIELD_LABEL: Record<string, string> = {
  title: "título del puesto",
  company: "quién publica",
  sector: "sector",
  location: "ubicación",
  remote: "modalidad",
  contractType: "tipo de contrato",
  description: "descripción del puesto",
  requirements: "requisitos",
  salaryMin: "salario mínimo",
  salaryMax: "salario máximo",
  salaryCurrency: "moneda",
  salaryPeriod: "periodo",
  expiresAt: "fecha de caducidad",
  currentPassword: "contraseña actual",
  newPassword: "contraseña nueva",
  username: "usuario",
  password: "contraseña",
};

/** Traduce el mensaje de Zod (en inglés) a una pista concreta en español. */
function describeIssue(msg: string): string | null {
  const tooSmall = msg.match(/expected string to have >=(\d+)/);
  if (tooSmall) return `mínimo ${tooSmall[1]} caracteres`;
  const tooBig = msg.match(/expected string to have <=(\d+)/);
  if (tooBig) return `máximo ${tooBig[1]} caracteres`;
  if (/expected int/.test(msg)) return "debe ser un número entero, sin decimales";
  if (/expected number/.test(msg)) return "debe ser un número";
  if (/invalid_sector/.test(msg)) return "sector no válido";
  return null;
}

/**
 * Convierte el `issues` de la API (el `flatten().fieldErrors` de Zod) en una
 * frase accionable. Sin esto, un 400 de validación llegaba al usuario como
 * "revisa los campos marcados" — sin marcar ninguno.
 */
export function fieldIssuesMessage(payload: unknown): string | null {
  const issues = (payload as { issues?: Record<string, string[]> } | null)?.issues;
  if (!issues || typeof issues !== "object") return null;
  const parts = Object.entries(issues)
    .map(([field, msgs]) => {
      const label = FIELD_LABEL[field] ?? field;
      const hint = Array.isArray(msgs) ? describeIssue(msgs[0] ?? "") : null;
      return hint ? `${label} (${hint})` : label;
    })
    .filter(Boolean);
  if (parts.length === 0) return null;
  return `Revisa: ${parts.join(" · ")}.`;
}

/** Traduce los códigos de error de la API a algo que un humano entienda. */
export function errorMessage(code: string): string {
  const map: Record<string, string> = {
    invalid_credentials: "Usuario o contraseña incorrectos.",
    account_blocked: "Tu cuenta está bloqueada. Contacta con el administrador.",
    challenge_failed: "No se pudo verificar el desafío anti-bot. Recarga e inténtalo de nuevo.",
    rate_limited: "Demasiados intentos. Espera unos minutos.",
    unauthorized: "Tu sesión ha caducado. Vuelve a entrar.",
    password_change_required: "Debes cambiar la contraseña antes de continuar.",
    invalid_input: "Revisa los campos marcados.",
    expiry_required: "Indica una fecha de caducidad.",
    expiry_in_past: "La fecha de caducidad ya ha pasado.",
    expiry_too_far: "La caducidad no puede superar los 3 meses.",
    salary_range_inverted: "El salario mínimo no puede superar al máximo.",
    salary_negative: "El salario no puede ser negativo.",
    username_taken: "Ese usuario ya existe.",
    not_found: "No encontrado.",
    jobs_unavailable: "El portal de empleo no está disponible ahora mismo.",
    pwned_password: "Esa contraseña aparece en filtraciones conocidas. Elige otra.",
  };
  return map[code] ?? "Algo ha fallado. Inténtalo de nuevo.";
}
